import React from 'react';

import { toSafeHtml } from './htmlSanitizer';
import './panelReport.css';

// A model can emit an object where the schema promises a string; rendering one as a React child
// throws "Objects are not valid as a React child", so every scalar goes through here first.
export const text = (value: unknown): string | null => {
	if (typeof value === 'string') return value.trim().length > 0 ? value : null;
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return null;
};

export const textList = (value: unknown): string[] => {
	if (!Array.isArray(value)) return [];
	return value.map(text).filter((entry): entry is string => entry !== null);
};

export const joinText = (value: unknown): string | null => {
	const entries = textList(value);
	return entries.length > 0 ? entries.join(', ') : null;
};

export const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

// Prose sections are model-authored HTML. `toSafeHtml` sanitises with a zero-attribute
// allow-list, and also renders a legacy plain-text row as paragraphs.
export const Html: React.FC<{ value: unknown }> = ({ value }) => {
	const html = toSafeHtml(value);
	if (!html) return null;
	return <div className="mg-prose mg-prose-scroll" dangerouslySetInnerHTML={{ __html: html }} />;
};

export interface RecordRow {
	label: string;
	value: React.ReactNode;
}

export const RecordCard: React.FC<{
	title?: string | null;
	extra?: React.ReactNode;
	rows?: RecordRow[];
	children?: React.ReactNode;
}> = ({ title, extra, rows = [], children }) => {
	const visible = rows.filter((row) => row.value !== null && row.value !== undefined && row.value !== '');
	if (!title && !extra && visible.length === 0 && !children) return null;
	// Flat surface, not a nested antd Card — design-system §10 forbids the nesting, §24 wants
	// a 1px border and no shadow.
	return (
		<div className="mg-record-card">
			{(title || extra) && (
				<div className="mg-record-card__head">
					{title && <span className="mg-record-card__title">{title}</span>}
					{extra}
				</div>
			)}
			{visible.map((row) => (
				<div key={row.label} className="mg-record-row">
					<span>{row.label}: </span>
					{row.value}
				</div>
			))}
			{children}
		</div>
	);
};

// The legacy `gr-food-tag` chip row, generalised: any list-valued field renders as chips rather
// than a comma-joined sentence (temp/gene.codes/js/pubscripts1.js:518-522).
export const TagRow: React.FC<{ items: string[] }> = ({ items }) => {
	if (items.length === 0) return null;
	return (
		<span className="mg-record-tags">
			{items.map((item, index) => (
				<span key={index} className="mg-record-tag">
					{item}
				</span>
			))}
		</span>
	);
};

// The chip row as a row VALUE: an empty list collapses the whole row rather than printing a
// bare label, which is what RecordCard's own filter needs.
export const tags = (value: unknown): React.ReactNode => {
	const items = textList(value);
	return items.length > 0 ? <TagRow items={items} /> : null;
};

export const SubHeading: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
	<div className="mg-subheading">{children}</div>
);

export const BulletList: React.FC<{ items: unknown }> = ({ items }) => {
	const entries = textList(items);
	if (entries.length === 0) return null;
	return (
		<ul className="mg-bullet-list">
			{entries.map((entry, index) => (
				<li key={index}>{entry}</li>
			))}
		</ul>
	);
};

// Stored legacy rows carry the flat-string form of these arrays, so a string item renders as a
// plain paragraph rather than being dropped (temp/gene.codes/js/pubscripts1.js:372-379).
export function DualShapeList<T>(props: {
	items?: Array<T | string>;
	render: (item: T, index: number) => React.ReactNode;
}): React.ReactElement | null {
	const items = Array.isArray(props.items) ? props.items : [];
	if (items.length === 0) return null;
	return (
		<>
			{items.map((item, index) => {
				if (typeof item === 'string') {
					const body = text(item);
					return body ? (
						<div key={index} className="mg-record-card">
							<div className="mg-record-card__prose">{body}</div>
						</div>
					) : null;
				}
				if (item && typeof item === 'object') {
					return <React.Fragment key={index}>{props.render(item as T, index)}</React.Fragment>;
				}
				return null;
			})}
		</>
	);
}
